import React, {useState} from "react";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";

import {
  removeBrokerDataMutation,
  useInvoicesByOrderIdQuery,
  useLazyDeclarationsByOrderIdQuery,
  useLazyDeclarationSummaryByOrderIdQuery,
  useLazyGetBankCommissionsByOrderIdQuery,
  useLazyGetBrokerDataByCodeQuery,
  useLazyGetOrderSummaryByIdQuery,
  useLazyGetOrderSummaryInvoicesByIdQuery,
  useLazyGetRouteByOrderIdQuery,
  useLazyInvoicesByOrderIdQuery,
  useLazySummaryByOrderIdQuery,
  useProductsByInvoiceIdQuery,
  useRemoveInvoiceMutation,
  useSetCompletedByOrderIdMutation,
  useUpdateBankCommissionsByInvoiceId
} from "../../../../../../../../../api/calc";

import { Completed } from '@vedmarket/ui-kit';
import MenuButton from '../../../../../../../../common/menuButton';
import ModalBig from "../../../../../../../../common/modalBig";
import ModalAction from "../../../../../../../../common/modalAction";

import EditInvoice from "./../../components/invoiceDetails/components/editInvoice";
import Products from "./../../components/invoiceDetails/components/products";

import {useGetCalcIdFromRoute} from "../../../../../../../../../utils/hooks";
import {getFlag} from "../../../../../delivery/helpers";
import {useDemoInvoiceDetails} from 'components/cabinet/demo/steps/packaging/use-packaging';
import {isCalculationViewModeError} from 'utils/helpers';
import {useCalculationReadOnly} from '../../../../../../calculation-read-only';

type Props = {
    openByDefault?: boolean
    invoice?: InvoiceParamsOutput
    testDriveScenarioSlug?: string
    onCloseInvoiceForm?(): void
}

const InvoiceDetails = ({openByDefault, invoice, testDriveScenarioSlug, onCloseInvoiceForm} : Props) => {

  const { t } = useTranslation()

  const orderId = useGetCalcIdFromRoute()

  const [ setCompleted ] = useSetCompletedByOrderIdMutation()
  const [ removeBroker ] = removeBrokerDataMutation()
  const [ removeInvoice ] = useRemoveInvoiceMutation()

  const [ openEditModal, setOpenEditModal ] = useState(false);

  const [ shouldOpenRemoveModal, setShouldOpenRemoveModal ] = useState(false)

  const { refetch: refetchProductsByInvoiceId, data: productsByInvoiceId } = useProductsByInvoiceIdQuery(invoice?.id || 0, {skip: !invoice?.id})

  const [ triggerFetchDeclaration ] = useLazyDeclarationsByOrderIdQuery();
  const [ triggerSummaryPackaging ] = useLazySummaryByOrderIdQuery();
  const [ triggerRouteByOrderId ] = useLazyGetRouteByOrderIdQuery();
  const [ triggerOrderSummary ] = useLazyGetOrderSummaryInvoicesByIdQuery();
  const [ triggerSummaryDeclaration ] = useLazyDeclarationSummaryByOrderIdQuery();
  const [ triggerFetchSummaryDeclaration ] = useLazyDeclarationSummaryByOrderIdQuery();
  const [ triggerFetchBankCommissionsRequest ] = useLazyGetBankCommissionsByOrderIdQuery()
  const [ triggerFetchOrderSummaryRequest ] = useLazyGetOrderSummaryByIdQuery()
  const [ triggerGetBrokerDataByCode ] = useLazyGetBrokerDataByCodeQuery();
  const [ triggerGetInvoices ] = useLazyInvoicesByOrderIdQuery();

  const { refetch: refetchInvoicedByOrderId } = useInvoicesByOrderIdQuery(orderId, { skip: !orderId })

  const getCompletedInfo = () => [
    {
      label: t('calc__text__main__descriptionOfTheGoods'),
      value: invoice?.description
    },
    ...(invoice?.supplier
      ? [
        {
          label: t('calc__text__main__supplierCompany'),
          value: `${invoice?.supplier.name}${invoice?.supplier.legal_address ? ` (${invoice.supplier.legal_address})` : ''}`
        },
      ]
      : []),
    {
      label: t('calc__text__main__incoterms'),
      value: invoice?.incoterms?.toUpperCase()
    },
    {
      label: t('calc__text__main__cityOfDeliveryExplanation'),
      value: <span className='invoice__flag-info'>{getFlag({code: invoice?.location?.country?.code, width: '22px', marginRight: '6px'  })} {invoice?.location?.name}</span>
    },
  ]

  const handleAddSuccess = () => {
    refetchInvoicedByOrderId()
    setOpenEditModal(false)
  }

  const onEdit = () => {
    setOpenEditModal(true)
  }

  const handleRemoveInvoice = async (invoiceId?: number | string) => {
    if (invoiceId) {
      const result = await removeInvoice({orderId, invoiceId: Number(invoiceId)})

      if (isCalculationViewModeError(result)) {
        return;
      }

      setShouldOpenRemoveModal(false)
      await refetchInvoicedByOrderId()
    }
  }

  const invoiceId = invoice?.id;

  const demoPlayback = useDemoInvoiceDetails(invoiceId)
  const readOnly = useCalculationReadOnly()
  const shouldShowCompletedInvoice = demoPlayback.shouldShowCompleted || readOnly

  const getCompletedInvoiceCaption = () =>
    `${t('common__text__invoice')}  № ${invoice?.invoice_num} ${t('common__text__from')} ${moment(invoice?.invoice_date).format('DD.MM.YYYY')}`;

  const dispatch = useDispatch()

  const handleRefetchRequests = async (options?: { skipOrderSummary?: boolean }) => {
    const skipOrderSummary = options?.skipOrderSummary;

    await triggerSummaryPackaging(orderId);
    if (!skipOrderSummary) {
      await triggerRouteByOrderId(orderId);
    }
    await triggerFetchSummaryDeclaration(orderId);
    await triggerFetchDeclaration(orderId);
    await triggerSummaryDeclaration(orderId)
    await triggerGetInvoices(orderId);
    await triggerOrderSummary(orderId);
    if (!skipOrderSummary) {
      await triggerFetchOrderSummaryRequest(orderId);
    }

    await triggerGetBrokerDataByCode(orderId).then((data) => {

      const productCodes = new Set(productsByInvoiceId?.map(product => product.code));

      const missingProducts = productsByInvoiceId
        ?.filter(product => !productCodes.has(product.code));

      missingProducts?.map(async (missingProduct) => {
        await removeBroker({
          orderId: orderId,
          code: missingProduct.code,
          countryCode: missingProduct.country_code,
        });
      })

    });

    await triggerFetchBankCommissionsRequest({orderId}).then(data => {
      const bankCommissionInvoices = data?.data?.invoices;
      bankCommissionInvoices?.map((bankCommissionInvoice) => {

        const bankingCommissions = bankCommissionInvoice.banking_commissions;

        bankingCommissions.forEach((bankingCommission) => {
          const dataToSend = {
            orderId,
            invoiceNumber: bankCommissionInvoice.invoice_num,
            // id: bankingCommission.id,
            isRemove: true,
            data: {
              id: bankingCommission.id,
            }
          }

          // @ts-ignore
          dispatch(useUpdateBankCommissionsByInvoiceId(dataToSend));
        })
      })
    });

    !skipOrderSummary && await setCompleted({
      orderId,
      customs: false,
      certification: false,
      banking_commissions: false,
    })
  }

  const handleCloseInvoiceForm = () => {
    if (onCloseInvoiceForm) {
      onCloseInvoiceForm();
    }
    handleRefetchRequests();
  }

  return (
    <>
      {shouldShowCompletedInvoice ? <Completed
        info={getCompletedInfo()}
        onEdit={()=> onEdit()}
        MenuComponent={MenuButton}
        onRemove={() => setShouldOpenRemoveModal(true)}
        isFullRow
        caption={getCompletedInvoiceCaption()}
      /> : <EditInvoice
        handleSuccess={handleAddSuccess}
        isFirstInvoice={!demoPlayback.isDemoMode}
        currentInvoice={demoPlayback.isDemoMode ? invoice : undefined}
        onCloseInvoiceForm={handleCloseInvoiceForm}
      />}
      {demoPlayback.shouldShowProducts && <Products
        productCount={invoice?.product_count || 0}
        openByDefault={openByDefault}
        invoice={invoice}
        testDriveScenarioSlug={testDriveScenarioSlug}
        handleRefetchRequests={handleRefetchRequests}
      />}
      {openEditModal && <ModalBig
        isOpen
        onClose={() => setOpenEditModal(false)}
        title={t('calc__text__main__editInvoice')}
      >
        <EditInvoice
          handleSuccess={handleAddSuccess}
          hasCancelButton
          onClose={() => setOpenEditModal(false)}
          currentInvoice={invoice}
        />
      </ModalBig>}
      {shouldOpenRemoveModal && <ModalAction
        isOpen={shouldOpenRemoveModal}
        onContinue={handleRemoveInvoice}
        index={invoiceId}
        onClose={() => setShouldOpenRemoveModal(false) }
        title={t('common__text__removeInvoice')}
        description={<>
          {t('actions__message__removeConfirm')}&nbsp;
          {t('common__text__invoice')}&nbsp;
          <span className='text_bold'>{`«${invoice?.invoice_num}»?` }</span>
        </>}
        buttonYesText={t('actions__text__yesDelete')}
      />}
    </>
  )
}

export default InvoiceDetails
